// "Talk to Arlo HQ": one big, hands-free voice assistant.
//
//  - Ask anything → Claude answers in a short, spoken style; each sentence
//    is spoken as soon as it arrives (Azure neural voice when configured,
//    else the device's best British voice), with live captions.
//  - Say what happened ("he's just done a wee outside") → it proposes log
//    entries and asks you to confirm (tap, or just say "yes").
//  - Hands-free: after an answer it listens again for a follow-up.
//
// Without AI it still logs, and answers from the built-in FAQ.

import { Check, MessageSquareText, Mic, Square, Volume2, VolumeX, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { FAQ } from '../../content/faq.ts';
import { SKILLS } from '../../content/training.ts';
import { ask, useAskInfo } from '../../data/ask.ts';
import { api } from '../../data/api.ts';
import { setDevicePrefs } from '../../data/device.ts';
import { useDevice, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { commitProposals } from '../../data/logProposals.ts';
import { SentenceSplitter, Speaker, canListen, canSpeakOnDevice, listen, looksLikeQuestion, setServerSpeech, unlockAudio, type Listening } from '../../data/voice.ts';
import { hasMic } from '../../data/recorder.ts';

function hasMicHint(): string {
  return hasMic
    ? 'Listening needs a speech key on the server in this browser — type below for now.'
    : 'This browser can’t use the microphone — type below instead.';
}
import { describeProposal, parseLogText, type LogProposal } from '../../domain/parse.ts';
import { navigate } from '../../app/router.ts';
import { Prose } from '../../ui/bits.tsx';

// --- Open/close from anywhere ------------------------------------------------------------

let state = { open: false, listen: false, question: undefined as string | undefined, n: 0 };
const subs = new Set<() => void>();
const emit = () => subs.forEach((l) => l());

/** Open the assistant. Call from a tap so audio and the mic are allowed to start. */
export function openVoice(opts: { listen?: boolean; question?: string } = {}): void {
  unlockAudio();
  state = { open: true, listen: opts.listen ?? !opts.question, question: opts.question, n: state.n + 1 };
  emit();
}

function closeVoice() {
  state = { ...state, open: false };
  emit();
}

export function VoiceHost() {
  const s = useSyncExternalStore(
    (l) => {
      subs.add(l);
      return () => subs.delete(l);
    },
    () => state,
  );
  if (!s.open) return null;
  return <VoiceAssistant key={s.n} autoListen={s.listen} question={s.question} onClose={closeVoice} />;
}

// --- The assistant -------------------------------------------------------------------------

type Phase = 'idle' | 'listening' | 'thinking' | 'speaking' | 'confirm';

const YES = /^(yes|yeah|yep|yup|sure|correct|log it|that's right|thats right|go on|please|ok|okay|aye)\b/i;
const NO = /^(no|nope|nah|cancel|stop|wrong|don't|do not)\b/i;

function faqAnswer(q: string): string | undefined {
  const words = q.toLowerCase().split(/\W+/).filter((w) => w.length > 3);
  const best = FAQ.map((f) => ({ f, score: words.filter((w) => `${f.q} ${f.tags.join(' ')}`.toLowerCase().includes(w)).length }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)[0];
  return best ? best.f.a : undefined;
}

const skillName = (id: string) => SKILLS.find((s) => s.id === id)?.name;

function VoiceAssistant({ autoListen, question, onClose }: { autoListen: boolean; question?: string; onClose: () => void }) {
  const d = useDevice();
  const info = useAskInfo();
  const profile = useProfile();
  const status = useLiveStatus(60_000);
  const [phase, setPhase] = useState<Phase>('idle');
  const [heard, setHeard] = useState('');
  const [answer, setAnswer] = useState('');
  const [note, setNote] = useState('');
  const [proposals, setProposals] = useState<(LogProposal & { on: boolean })[]>([]);
  const mic = useRef<Listening | undefined>(undefined);
  const speaker = useRef<Speaker | undefined>(undefined);
  const abort = useRef<AbortController | undefined>(undefined);
  const phaseRef = useRef<Phase>('idle');
  const closed = useRef(false);
  const streaming = useRef(false);
  const started = useRef(false);
  /** The person stopped the answer — don't start listening again by ourselves. */
  const userStopped = useRef(false);
  const [level, setLevel] = useState(0);
  const followUp = useRef(false);
  const infoRef = useRef(info);
  infoRef.current = info;
  const proposalsRef = useRef(proposals);
  proposalsRef.current = proposals;

  /** AI status, fetched now if it hasn't arrived yet. */
  const getInfo = async () => {
    if (infoRef.current) return infoRef.current;
    const i = await api<{ enabled: boolean; voice: string; stt?: boolean }>('/api/ask').catch(() => ({ enabled: false, voice: 'device', stt: false }));
    setServerSpeech(!!i.stt);
    return i;
  };
  const name = profile.name || 'Arlo';
  const night = document.documentElement.dataset.theme === 'dark' && d.kiosk;

  const go = (p: Phase) => {
    phaseRef.current = p;
    setPhase(p);
  };

  const newSpeaker = (onIdle?: () => void) => {
    speaker.current?.stop();
    const sp = new Speaker({ neural: infoRef.current?.voice === 'neural', volume: night ? 0.45 : 1, onStart: () => go(phaseRef.current === 'confirm' ? 'confirm' : 'speaking'), onIdle });
    speaker.current = sp;
    return sp;
  };

  const stopAll = () => {
    mic.current?.abort();
    mic.current = undefined;
    speaker.current?.stop();
    abort.current?.abort();
  };

  useEffect(() => {
    // (React StrictMode mounts twice in development: reset, and only start once.)
    closed.current = false;
    const first = !started.current;
    started.current = true;
    if (question) {
      if (first) void handle(question);
    } else if (autoListen && !mic.current) {
      // Server listening (WebViews) depends on the server's speech key — check first.
      if (canListen()) startListening();
      else
        void getInfo().then(() => {
          if (closed.current) return;
          if (canListen()) startListening();
          else setNote(hasMicHint());
        });
    }
    return () => {
      closed.current = true;
      stopAll();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * 'fresh' clears the screen; 'followup' keeps the last answer up while
   * listening for another question; 'confirm' waits for "yes"/"no".
   */
  function startListening(mode: 'fresh' | 'followup' | 'confirm' = 'fresh') {
    if (closed.current) return;
    const forConfirm = mode === 'confirm';
    speaker.current?.stop();
    setNote('');
    followUp.current = mode === 'followup';
    if (mode === 'fresh') {
      setHeard('');
      setAnswer('');
      setProposals([]);
    }
    const l = listen({
      onLevel: (v) => setLevel(v),
      onText: (t) => {
        if (!forConfirm) {
          if (followUp.current) {
            // They've started a new question: clear the old answer.
            followUp.current = false;
            setAnswer('');
          }
          setHeard(t);
        }
      },
      onDone: (t) => {
        mic.current = undefined;
        if (closed.current) return;
        if (forConfirm) return void confirmByVoice(t);
        if (!t) {
          // Silence after an answer is normal — just stop listening quietly.
          if (!followUp.current) setNote(canListen() ? 'Didn’t catch that — tap the orb and try again.' : '');
          followUp.current = false;
          return go('idle');
        }
        void handle(t);
      },
      onError: (e) => {
        mic.current = undefined;
        setNote(e === 'not-allowed' || e === 'service-not-allowed' ? 'Microphone blocked — allow it for this site in the browser settings.' : 'Couldn’t hear properly — try again.');
        // While confirming, keep the Log it / Not that buttons.
        if (!forConfirm) go('idle');
      },
    });
    if (!l) {
      setNote('Tap the orb to talk.');
      return go('idle');
    }
    mic.current = l;
    go(forConfirm ? 'confirm' : 'listening');
  }

  function confirmByVoice(t: string) {
    if (YES.test(t.trim())) commit();
    else if (NO.test(t.trim())) cancelLog();
    // Anything else: leave the buttons up.
  }

  async function handle(text: string) {
    // One conversation at a time: a new request cancels the mic, speech and any answer in flight.
    mic.current?.abort();
    mic.current = undefined;
    speaker.current?.stop();
    abort.current?.abort();
    streaming.current = false;
    userStopped.current = false;
    setHeard(text);
    setAnswer('');
    setProposals([]);
    const question = looksLikeQuestion(text);
    const ai = (await getInfo()).enabled;
    if (!question) {
      let found = parseLogText(text);
      if (!found.length && ai) {
        go('thinking');
        found = await api<{ entries: LogProposal[] }>('/api/parse', { body: { text } })
          .then((r) => r.entries)
          .catch(() => []);
      }
      if (found.length) return proposeLog(found);
    }
    return answerQuestion(text, ai);
  }

  function proposeLog(found: LogProposal[]) {
    setProposals(found.map((p) => ({ ...p, on: true })));
    go('confirm');
    const what = found.map((p) => describeProposal(p, skillName).split(' · ')[0].toLowerCase()).join(', ');
    const line = `Got it: ${what}. Shall I log that?`;
    setAnswer(line);
    if (d.speak) {
      const sp = newSpeaker(() => {
        if (phaseRef.current === 'confirm' && d.handsFree && canListen() && !closed.current) startListening('confirm');
      });
      sp.say(line);
    } else if (d.handsFree && canListen()) startListening('confirm');
  }

  function commit() {
    // Read the ref: a spoken "yes" arrives in a callback from an earlier render.
    const chosen = proposalsRef.current.filter((p) => p.on);
    if (phaseRef.current !== 'confirm' || !chosen.length) return;
    mic.current?.abort();
    commitProposals(chosen);
    setProposals([]);
    setAnswer('Logged. Good boy!');
    go('idle');
    if (d.speak) newSpeaker(() => go('idle')).say('Logged. Good boy!');
  }

  function cancelLog() {
    mic.current?.abort();
    setProposals([]);
    setAnswer('');
    setNote('No problem — nothing logged.');
    go('idle');
  }

  function finishAnswer() {
    go('idle');
    if (userStopped.current) return;
    if (d.handsFree && canListen() && !closed.current) setTimeout(() => !closed.current && phaseRef.current === 'idle' && startListening('followup'), 350);
  }

  async function answerQuestion(q: string, ai: boolean) {
    go('thinking');
    if (!ai) {
      const a = faqAnswer(q);
      const text = a ? a : `I can only answer from the built-in guide at the moment, and I couldn’t find that one. Try the Guide, or switch on AI in Azure.`;
      setAnswer(text);
      if (d.speak) {
        const sp = newSpeaker(() => go('idle'));
        const first = text.split(/(?<=[.!?])\s+/).slice(0, 3).join(' ');
        sp.say(first);
      } else go('idle');
      return;
    }
    const splitter = new SentenceSplitter();
    // The speaker can run dry between sentences while the answer is still
    // streaming — only finish once both the stream and the speech are done.
    const sp = d.speak ? newSpeaker(() => !streaming.current && finishAnswer()) : undefined;
    abort.current = new AbortController();
    streaming.current = true;
    try {
      await ask(q, {
        voice: true,
        signal: abort.current.signal,
        onText: (delta) => {
          setAnswer((a) => a + delta);
          if (sp) for (const s of splitter.feed(delta)) sp.say(s);
        },
      });
      streaming.current = false;
      if (sp) {
        for (const s of splitter.flush()) sp.say(s);
        if (!sp.speaking) finishAnswer();
      } else go('idle');
    } catch (err) {
      streaming.current = false;
      speaker.current?.stop();
      setAnswer((err as Error).message);
      go('idle');
    }
  }

  const tapOrb = () => {
    unlockAudio();
    if (phase === 'listening') return mic.current?.stop();
    if (phase === 'speaking') {
      userStopped.current = true;
      stopAll();
      return go('idle');
    }
    if (phase === 'thinking') {
      stopAll();
      return go('idle');
    }
    if (phase === 'confirm') return commit();
    startListening();
  };

  const suggestions = useMemo(() => {
    const s = status;
    const out = [
      s.sleep.asleep ? `How long should ${name} nap?` : `Is it time for a nap?`,
      `How much should he eat at his age?`,
      `What should we practise today?`,
      `How do we stop the biting?`,
      `Is he getting enough sleep?`,
      `He’s just done a wee outside`,
    ];
    return out;
  }, [status.sleep.asleep, name]); // eslint-disable-line react-hooks/exhaustive-deps

  const label: Record<Phase, string> = {
    idle: canListen() ? 'Tap to talk' : 'Type below',
    listening: 'Listening…',
    thinking: 'Thinking…',
    speaking: 'Tap to stop',
    confirm: 'Tap to log',
  };

  const [typed, setTyped] = useState('');

  return (
    <div className="voice" role="dialog" aria-modal="true" aria-label="Talk to Arlo HQ">
      <div className="voice-top">
        <div className="voice-brand">
          <span className="arlo-avatar">{profile.photo ? <img src={profile.photo} alt="" /> : <img src="./arlo.jpg" alt="" />}</span>
          <span>
            <strong>Arlo HQ</strong>
            <span className="small">
              {info === null ? '…' : info.enabled ? 'AI' : 'Guide answers — AI not switched on'}
              {info !== null && ` · ${info.voice === 'neural' ? 'natural voice' : canSpeakOnDevice() ? 'device voice' : 'captions only (add a speech key for a voice)'}`}
            </span>
          </span>
        </div>
        <div className="voice-tools">
          <button type="button" className="voice-tool" aria-pressed={d.speak} onClick={() => {
              if (d.speak) {
                // Muting mid-answer: keep the captions coming, but don't re-open the mic afterwards.
                userStopped.current = true;
                speaker.current?.stop();
              }
              setDevicePrefs({ speak: !d.speak });
            }} aria-label={d.speak ? 'Mute spoken answers' : 'Speak answers'}>
            {d.speak ? <Volume2 aria-hidden /> : <VolumeX aria-hidden />}
          </button>
          <button
            type="button"
            className="voice-tool"
            onClick={() => {
              onClose();
              navigate('/ask');
            }}
            aria-label="Open the conversation"
          >
            <MessageSquareText aria-hidden />
          </button>
          <button type="button" className="voice-tool" onClick={onClose} aria-label="Close">
            <X aria-hidden />
          </button>
        </div>
      </div>

      <div className="voice-stage">
        <button type="button" className={`orb orb-${phase}`} onClick={tapOrb} aria-label={label[phase]} style={{ ['--level' as string]: level.toFixed(2) }}>
          <span className="orb-ring r1" />
          <span className="orb-ring r2" />
          <span className="orb-core">{phase === 'speaking' || phase === 'thinking' ? <Square aria-hidden /> : phase === 'confirm' ? <Check aria-hidden /> : <Mic aria-hidden />}</span>
        </button>
        <span className="orb-label">{label[phase]}</span>

        {heard && <p className="voice-heard">“{heard}”</p>}
        {note && <p className="voice-note">{note}</p>}

        {phase === 'confirm' && proposals.length > 0 ? (
          <div className="voice-confirm">
            {proposals.map((p, i) => (
              <button key={i} type="button" className={`talk-proposal${p.on ? ' on' : ''}`} aria-pressed={p.on} onClick={() => setProposals((list) => list.map((x, j) => (j === i ? { ...x, on: !x.on } : x)))}>
                <Check aria-hidden />
                {describeProposal(p, skillName)}
              </button>
            ))}
            <div className="voice-confirm-actions">
              <button type="button" className="btn btn-primary btn-lg" onClick={commit} disabled={!proposals.some((p) => p.on)}>
                <Check className="btn-icon" aria-hidden /> <span>Log it</span>
              </button>
              <button type="button" className="btn btn-ghost btn-lg" onClick={cancelLog}>
                <span>Not that</span>
              </button>
            </div>
            {d.handsFree && canListen() && <p className="voice-note">Or just say “yes”.</p>}
          </div>
        ) : answer ? (
          <div className="voice-answer" aria-live="polite">
            <Prose text={answer} />
          </div>
        ) : (
          (phase === 'idle' || phase === 'listening') &&
          !heard && (
            <div className="voice-suggest">
              <p className="voice-hint">Ask anything about {name} — or tell me what he’s just done.</p>
              <div className="ask-suggest">
                {suggestions.map((s) => (
                  <button key={s} type="button" className="chip voice-chip" onClick={() => {
                      unlockAudio();
                      void handle(s);
                    }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )
        )}
      </div>

      <form
        className="voice-type"
        onSubmit={(e) => {
          e.preventDefault();
          if (!typed.trim()) return;
          unlockAudio();
          stopAll();
          void handle(typed.trim());
          setTyped('');
        }}
      >
        <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={canListen() ? 'Or type…' : 'Ask or tell Arlo HQ…'} />
        <button type="submit" className="btn btn-primary" disabled={!typed.trim()}>
          <span>Send</span>
        </button>
      </form>
    </div>
  );
}
